import { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import {
  ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM,
  ECOM_PRINT_DESIGN_ITEM_TYPE_LIST,
  S3_PUBLIC_URL,
  S3_PUBLIC_URL_OLD
} from '../../../../config/constants';
import { printDesignImagePositioningConfig } from '../printDesignImagePositioningConfig';
import { triggerNotification } from '../../../../helpers/notification';
import {
  dataUrlToFile,
  uploadFileToStorage
} from '../../../../helpers/awsHelper';
import { sanitizeText } from '../../../../helpers/format';
import textMetricsProvider from './textMetricsProvider';
import { FIT_MODE, layoutText } from './textLayoutEngine';
import {
  applyLayoutToFabricTextbox,
  getFabricLayoutOptions,
  repositionFabricTextbox
} from './fabricTextLayoutAdapter';

const clampZoom = (z) => Math.max(0.2, Math.min(40, z));

const isPositionDifferent = (posA, posB) =>
  Math.ceil(posA * 100) / 100 !== Math.ceil(posB * 100) / 100;

const FABRIC_CANVAS_DPI = 96; // default DPI for fabric.js
const UNIFORM_DPI = 300; // target DPI for high-resolution exports
const DEFAULT_TEXTBOX_SIZE = 100; // default width/height for textboxes when calculating with scale
const DEFAULT_TEXTBOX_TEXT = 'Have it your way!';
const DEFAULT_FONT_SIZE = 40;
// glyphs fit the container edge to edge, so a small print-safety margin is
// taken off the fitting box. It never changes the container's own geometry.
const DEFAULT_SAFETY_INSET_PERCENT = 2;
const MIN_PRINTABLE_FONT_SIZE = 6;

// geometry the save state is allowed to apply to a textbox container.
// anything else in the save state describes the text itself, not the container.
const CONTAINER_GEOMETRY_PROPS = [
  'left',
  'top',
  'scaleX',
  'scaleY',
  'angle',
  'width',
  'height',
  'visible',
  'flipX',
  'flipY',
  'opacity'
];

let designItemIdSequence = 0;

const createDesignItemId = (prefix) => {
  designItemIdSequence += 1;
  return `${prefix}-${Date.now()}-${designItemIdSequence}`;
};

const pickDefinedProps = (source, propNames) => {
  if (!source) return {};

  return propNames.reduce(
    (obj, name) =>
      source[name] === undefined ? obj : { ...obj, [name]: source[name] },
    {}
  );
};

const itemPropertiesConfig = {
  characterLimit: { type: 'number' },
  fontSize: { type: 'number' },
  allowMultiline: { type: 'boolean' },
  isUserInput: { type: 'boolean' },
  isRequired: { type: 'boolean' },
  noLetters: { type: 'boolean' },
  noNumbers: { type: 'boolean' },
  noSpecialCharacters: { type: 'boolean' },
  noSpaces: { type: 'boolean' }
};

const getDesignItemPropertiesListFromObject = (objProperties) => {
  if (!objProperties) return [];

  return Object.entries(objProperties).map(([key, value]) => {
    const propsConfig = itemPropertiesConfig[key];

    return {
      name: key,
      value:
        propsConfig?.type === 'boolean'
          ? value
            ? 'yes'
            : 'no'
          : value?.toString()
    };
  });
};

const getDesignItemPropertiesObjectFromList = (listProperties) => {
  if (!listProperties) return {};

  return listProperties.reduce((obj, { name, value }) => {
    const propsConfig = itemPropertiesConfig[name];

    return {
      ...obj,
      [name]: propsConfig
        ? propsConfig.type === 'boolean'
          ? value?.toLowerCase() === 'yes'
          : propsConfig.type === 'number'
          ? Number(value) || null
          : value
        : value
    };
  }, {});
};

const getCanvasSize = (canvas) => ({
  width: canvas.getWidth(),
  height: canvas.getHeight()
});

const getColorInfo = (color) => {
  if (!color) {
    return {
      isTransparent: true,
      hex: null,
      alpha: 0
    };
  }

  const fabricColor = new fabric.Color(color);

  const alpha = fabricColor.getAlpha();
  const hex = fabricColor.toHex().toLowerCase();

  return {
    hex,
    alpha,
    isTransparent: alpha === 0
  };
};

const getBoundingBox = (objects) => {
  if (!objects.length) return { left: 0, top: 0, width: 0, height: 0 };

  const bounds = objects.map((o) => o.getBoundingRect());

  const left = Math.min(...bounds.map((b) => b.left));
  const top = Math.min(...bounds.map((b) => b.top));
  const right = Math.max(...bounds.map((b) => b.left + b.width));
  const bottom = Math.max(...bounds.map((b) => b.top + b.height));

  return {
    left,
    top,
    width: right - left,
    height: bottom - top
  };
};

const createClipObject = (obj) => {
  const props = {
    left: obj.left,
    top: obj.top,
    scaleX: obj.scaleX,
    scaleY: obj.scaleY,
    angle: obj.angle,
    originX: obj.originX,
    originY: obj.originY,
    absolutePositioned: true,
    selectable: false,
    evented: false,
    objectCaching: false,
    fill: 'black', // clip path uses black fill to reveal
    stroke: null
  };

  switch (obj.type?.toLowerCase()) {
    case 'rect':
      return new fabric.Rect({
        ...props,
        width: obj.width,
        height: obj.height,
        rx: obj.rx,
        ry: obj.ry
      });
    case 'circle':
      return new fabric.Circle({ ...props, radius: obj.radius });
    case 'ellipse':
      return new fabric.Ellipse({ ...props, rx: obj.rx, ry: obj.ry });
    case 'polygon':
      return new fabric.Polygon(obj.points, props);
    case 'polyline':
      return new fabric.Polyline(obj.points, props);
    case 'path':
      return new fabric.Path(obj.path, props);
    case 'line':
      return new fabric.Line([obj.x1, obj.y1, obj.x2, obj.y2], props);
    default:
      // fallback: just reuse original but fill black, no stroke
      obj.set({ fill: 'black', stroke: null });
      return obj;
  }
};

const calculateItemPosition = (
  itemSaveState,
  item,
  backgroundGroup,
  { oldCanvasWidth, oldCanvasHeight } = {}
) => {
  const reverseScaleFactor = FABRIC_CANVAS_DPI / UNIFORM_DPI;

  const isImage =
    itemSaveState?.fkEcomPrintDesignItemType ===
      ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE ||
    item?.fkEcomPrintDesignItemType === ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE;
  const isTextbox =
    itemSaveState?.fkEcomPrintDesignItemType ===
      ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT ||
    item?.fkEcomPrintDesignItemType ===
      ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT;

  let scaleChangeX = 1;
  let scaleChangeY = 1;

  let { topPercent, leftPercent } = itemSaveState;

  // ------------------------------
  // Canvas resize recalculation
  // ------------------------------
  if (oldCanvasWidth && oldCanvasHeight) {
    const scaleXDiff = backgroundGroup.width / oldCanvasWidth;
    const scaleYDiff = backgroundGroup.height / oldCanvasHeight;

    if (isImage) {
      // preserve aspect ratio for images
      const uniformScale = Math.min(scaleXDiff, scaleYDiff);

      scaleChangeX = uniformScale;
      scaleChangeY = uniformScale;
    }
    else {
      // allow independent scaling
      scaleChangeX = scaleXDiff;
      scaleChangeY = scaleYDiff;
    }

    const oldLeftPx = (leftPercent / 100) * oldCanvasWidth;
    const oldTopPx = (topPercent / 100) * oldCanvasHeight;

    const scaledWidth = oldCanvasWidth * scaleChangeX;
    const scaledHeight = oldCanvasHeight * scaleChangeY;

    const leftAdjustment = (backgroundGroup.width - scaledWidth) / 2;

    const topAdjustment = (backgroundGroup.height - scaledHeight) / 2;

    const newLeftPx = leftAdjustment + oldLeftPx * scaleChangeX;

    const newTopPx = topAdjustment + oldTopPx * scaleChangeY;

    leftPercent = (newLeftPx / backgroundGroup.width) * 100;
    topPercent = (newTopPx / backgroundGroup.height) * 100;
  }
  const scaleX =
    itemSaveState.scaleX > 0
      ? itemSaveState.scaleX * scaleChangeX * reverseScaleFactor
      : 0;

  const scaleY =
    itemSaveState.scaleY > 0
      ? itemSaveState.scaleY * scaleChangeY * reverseScaleFactor
      : 0;

  const left =
    backgroundGroup.left +
    (leftPercent / 100) * backgroundGroup.width -
    backgroundGroup.width / 2;

  const top =
    backgroundGroup.top +
    (topPercent / 100) * backgroundGroup.height -
    backgroundGroup.height / 2;

  let leftCornerOffset = 0;
  let topCornerOffset = 0;
  if (isImage && item) {
    leftCornerOffset = (item.width * scaleX) / 2;
    topCornerOffset = (item.height * scaleY) / 2;
  }
  else if (isTextbox) {
    leftCornerOffset = (DEFAULT_TEXTBOX_SIZE * scaleX) / 2;
    topCornerOffset = (DEFAULT_TEXTBOX_SIZE * scaleY) / 2;
  }

  return {
    left: left + leftCornerOffset,
    top: top + topCornerOffset,
    scaleX,
    scaleY,
    angle: itemSaveState.angle || 0
  };
};

const calculatePercentPosition = (img, bgGroup) => {
  const scaleFactor = UNIFORM_DPI / FABRIC_CANVAS_DPI;

  const isImage =
    img.fkEcomPrintDesignItemType === ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE;

  // Images remain uniform
  const scaleX = isImage ? Math.min(img.scaleX, img.scaleY) : img.scaleX;
  const scaleY = isImage ? Math.min(img.scaleX, img.scaleY) : img.scaleY;
  const widthOffset = (img.width * scaleX) / 2;
  const heightOffset = (img.height * scaleY) / 2;

  const leftPercent =
    ((bgGroup.left - bgGroup.width / 2 - (img.left - widthOffset)) /
      bgGroup.width) *
    100 *
    -1;

  const topPercent =
    ((bgGroup.top - bgGroup.height / 2 - (img.top - heightOffset)) /
      bgGroup.height) *
    100 *
    -1;

  return {
    leftPercent,
    topPercent,
    scaleX: img.scaleX < 0.001 ? 0 : img.scaleX * scaleFactor,
    scaleY: img.scaleY < 0.001 ? 0 : img.scaleY * scaleFactor,
    topOffset: (bgGroup.height * (topPercent / 100)) / FABRIC_CANVAS_DPI,
    bottomOffset:
      ((bgGroup.height * (topPercent / 100) +
        img.height * scaleY -
        bgGroup.height) /
        FABRIC_CANVAS_DPI) *
      -1,
    leftOffset: (bgGroup.width * (leftPercent / 100)) / FABRIC_CANVAS_DPI,
    rightOffset:
      ((bgGroup.width * (leftPercent / 100) +
        img.width * scaleX -
        bgGroup.width) /
        FABRIC_CANVAS_DPI) *
      -1
  };
};

const getTextObjectFont = (textObj) => ({
  fontFamily: textObj.fontFamily,
  fontStyle: textObj.fontStyle,
  fontWeight: textObj.fontWeight
});

// canvas fillText silently falls back to a default face when an @font-face has
// not finished loading, so every font used by an export has to be resolved first
const loadFontsForTextObjects = async (textObjects) => {
  if (!textObjects.length || !document.fonts?.load) return;

  const fontSpecs = [
    ...new Set(
      textObjects
        .filter((obj) => obj.fontFamily)
        .map((obj) => {
          const style = obj.fontStyle || 'normal';
          const weight = obj.fontWeight || 'normal';
          const size = Math.max(
            1,
            Math.ceil(obj.fontSize || DEFAULT_FONT_SIZE)
          );

          return `${style} ${weight} ${size}px "${obj.fontFamily}"`;
        })
    )
  ];

  await Promise.all(
    fontSpecs.map((spec) =>
      document.fonts.load(spec).catch((err) => {
        console.warn(`Failed to load font for export: ${spec}`, err);
      })
    )
  );

  await document.fonts.ready;
};

// the headless and export paths have to resolve fonts before the text is
// *sized*, not just before it is rendered, or a fallback-derived size gets
// baked into the print file
const resolveFontsAndRefitTextObjects = async (textObjects) => {
  if (!textObjects.length) return;

  await loadFontsForTextObjects(textObjects);

  textObjects.forEach((textObj) => {
    // a face that never arrived leaves the refit measured against the fallback,
    // so the flag is re-derived rather than cleared: the layout ships flagged
    // provisional and the API recomputes it instead of trusting it
    textObj.set({
      isProvisionalTextLayout: !textMetricsProvider.isFontReady(
        getTextObjectFont(textObj)
      )
    });
    textObj.fire('modified');
  });
};

const resolveTextLayoutOptions = (textObj) => {
  const itemProperties = textObj.itemProperties || {};
  const insetPercent = Number(itemProperties.safetyInsetPercent);
  const safetyInsetPercent = Number.isFinite(insetPercent)
    ? insetPercent
    : DEFAULT_SAFETY_INSET_PERCENT;
  const safetyInset = safetyInsetPercent / 100;

  return {
    ...getFabricLayoutOptions(),
    allowMultiline: itemProperties.allowMultiline !== false,
    fitMode:
      itemProperties.fitMode === FIT_MODE.BAND ? FIT_MODE.BAND : FIT_MODE.INK,
    insetX: safetyInset,
    insetY: safetyInset,
    textAlign: textObj.textAlign,
    strokeWidthEm:
      textObj.stroke && textObj.strokeWidth > 0 && textObj.fontSize > 0
        ? textObj.strokeWidth / textObj.fontSize
        : 0,
    minFontSize: MIN_PRINTABLE_FONT_SIZE,
    currentFontSize: textObj.fontSize
  };
};

// canvas measurement silently falls back to a default face when an @font-face
// has not resolved, so anything sized that way is marked provisional and
// re-sized the moment the real face arrives
const scheduleFontReadyRefit = (textObj, onFontReady) => {
  const font = getTextObjectFont(textObj);

  if (textMetricsProvider.isFontReady(font)) {
    textObj.set({ isProvisionalTextLayout: false, hasPendingFontLoad: false });
    return;
  }

  textObj.set({ isProvisionalTextLayout: true });
  if (textObj.hasPendingFontLoad) return;

  textObj.set({ hasPendingFontLoad: true });
  textMetricsProvider.loadFont(font).then((isReady) => {
    textObj.set({ hasPendingFontLoad: false });
    if (!isReady) return;

    textObj.set({ isProvisionalTextLayout: false });
    onFontReady();
    textObj.canvas?.requestRenderAll();
  });
};

const useFabricCanvasActions = () => {
  const isZoomToFitRef = useRef(true);
  const backgroundGroupRef = useRef(null);
  const outlineGroupRef = useRef(null);
  const clipPathRef = useRef(null);
  const overlayGuideLineClipGroupRef = useRef(null);
  const verticalGuideRef = useRef(null);
  const horizontalGuideRef = useRef(null);
  const isRestoring = useRef(false);
  const isMovingObject = useRef(false);
  const [zoomLevel, setZoomLevel] = useState('zoomToFit');
  const [selectedObjectPosition, setSelectedObjectPosition] = useState({
    id: null,
    left: 0,
    top: 0,
    scaleX: 1,
    scaleY: 1,
    angle: 0
  });
  const GUIDE_THRESHOLD = 1;
  const MAX_HISTORY = 50;

  const setupPanAndZoom = (canvas, canvasOverlay) => {
    let isPanning = false;
    let lastX = 0;
    let lastY = 0;

    canvas.on('mouse:down', ({ e }) => {
      if (e.button === 2) {
        isPanning = true;
        lastX = e.clientX;
        lastY = e.clientY;
        e.preventDefault();
      }
    });

    canvas.on('mouse:move', ({ e }) => {
      if (!isPanning) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      const vpt = canvas.viewportTransform;
      vpt[4] += dx;
      vpt[5] += dy;

      const vptOverlay = canvasOverlay.viewportTransform;
      vptOverlay[4] += dx;
      vptOverlay[5] += dy;

      canvas.discardActiveObject();
      canvasOverlay.requestRenderAll();
      canvas.requestRenderAll();
    });

    canvas.on('mouse:up', ({ e }) => {
      if (e.button === 2) isPanning = false;
    });

    canvas.on('mouse:wheel', ({ e }) => {
      isZoomToFitRef.current = false;

      const zoom = clampZoom(canvas.getZoom() * 0.999 ** e.deltaY);
      canvas.zoomToPoint(new fabric.Point(e.offsetX, e.offsetY), zoom);
      canvasOverlay.zoomToPoint(new fabric.Point(e.offsetX, e.offsetY), zoom);

      setZoomLevel(zoom);

      e.preventDefault();
      e.stopPropagation();
    });
  };

  const setupGuides = (canvas, overlayCanvas) => {
    canvas.on('object:moving', ({ target }) => {
      if (!target || !backgroundGroupRef.current) return;

      const bgCenter = backgroundGroupRef.current.getCenterPoint();
      const objectCenter = target.getCenterPoint();

      const snappedX = Math.abs(objectCenter.x - bgCenter.x) < GUIDE_THRESHOLD;
      const snappedY = Math.abs(objectCenter.y - bgCenter.y) < GUIDE_THRESHOLD;

      // snap using center-based logic
      if (snappedX || snappedY) {
        const newX = snappedX ? bgCenter.x : objectCenter.x;
        const newY = snappedY ? bgCenter.y : objectCenter.y;
        target.setPositionByOrigin(
          new fabric.Point(newX, newY),
          'center',
          'center'
        );
      }

      // show guide lines at bgGroup center instead of canvas center
      if (snappedX && !verticalGuideRef.current) {
        verticalGuideRef.current = new fabric.Line(
          [bgCenter.x, 0, bgCenter.x, canvas.getHeight()],
          {
            stroke: '#c40000',
            strokeWidth: 0.4,
            selectable: false,
            evented: false,
            excludeFromExport: true,
            clipPath: overlayGuideLineClipGroupRef.current
          }
        );
        overlayCanvas.add(verticalGuideRef.current);
      }
      else if (!snappedX && verticalGuideRef.current) {
        overlayCanvas.remove(verticalGuideRef.current);
        verticalGuideRef.current = null;
      }

      if (
        snappedY &&
        !horizontalGuideRef.current &&
        backgroundGroupRef.current
      ) {
        const bgBounds = backgroundGroupRef.current.getBoundingRect(true); // true = absolute coords
        const { y } = backgroundGroupRef.current.getCenterPoint();

        horizontalGuideRef.current = new fabric.Line(
          [bgBounds.left, y, bgBounds.left + bgBounds.width, y],
          {
            stroke: '#c40000',
            strokeWidth: 0.4,
            selectable: false,
            evented: false,
            excludeFromExport: true,
            clipPath: overlayGuideLineClipGroupRef.current
          }
        );

        overlayCanvas.add(horizontalGuideRef.current);
      }
      else if (!snappedY && horizontalGuideRef.current) {
        overlayCanvas.remove(horizontalGuideRef.current);
        horizontalGuideRef.current = null;
      }

      canvas.requestRenderAll();
      overlayCanvas.requestRenderAll();
    });

    canvas.on('mouse:up', () => {
      if (verticalGuideRef.current) {
        overlayCanvas.remove(verticalGuideRef.current);
        verticalGuideRef.current = null;
      }
      if (horizontalGuideRef.current) {
        overlayCanvas.remove(horizontalGuideRef.current);
        horizontalGuideRef.current = null;
      }
      overlayCanvas.requestRenderAll();
    });
  };

  const onTrackObjectPosition = (object) => {
    if (object) {
      isMovingObject.current = true;

      const {
        leftPercent,
        topPercent,
        scaleX,
        scaleY
      } = calculatePercentPosition(object, backgroundGroupRef.current);

      setSelectedObjectPosition({
        id: object.id,
        left: object.left,
        top: object.top,
        leftPercent,
        topPercent,
        scaleX,
        scaleY,
        angle: object.angle
      });
    }
  };

  const setupObjectTracking = (setHistoryState, canvas) => {
    // Track object position changes
    canvas.on('object:moving', (e) => onTrackObjectPosition(e.target));
    canvas.on('object:scaling', (e) => onTrackObjectPosition(e.target));

    // Track object selection
    const onSelectObject = (e) => {
      setHistoryState((prev) => ({
        ...prev,
        selectedObjects: e.selected.map((obj) => obj.toObject(['id', 'src']))
      }));
      isMovingObject.current = false;
    };
    canvas.on('selection:created', onSelectObject);
    canvas.on('selection:updated', onSelectObject);
    canvas.on('selection:cleared', () => {
      setHistoryState((prev) => ({
        ...prev,
        selectedObjects: []
      }));
    });
  };

  const fitTextboxFontSize = ({ textObj, container }) => {
    const layout = layoutText(
      {
        text: textObj.text,
        font: getTextObjectFont(textObj),
        boxWidth: container.width * container.scaleX,
        boxHeight: container.height * container.scaleY,
        options: resolveTextLayoutOptions(textObj)
      },
      textMetricsProvider
    );

    // nothing measurable (empty text, or a collapsed container): keep the last
    // size so the box does not collapse and snap back on the next keystroke
    if (!layout) {
      repositionFabricTextbox(textObj, container);
      return null;
    }

    applyLayoutToFabricTextbox(textObj, container, layout);
    return layout;
  };

  const setupTextboxContainerSync = (textObj, textboxContainer) => {
    const syncTextboxWithContainer = () => {
      fitTextboxFontSize({ textObj, container: textboxContainer });
      scheduleFontReadyRefit(textObj, syncTextboxWithContainer);
    };

    textObj.on('modified', syncTextboxWithContainer);
    textboxContainer.on('scaling', syncTextboxWithContainer);
    textboxContainer.on('modified', syncTextboxWithContainer);

    textboxContainer.on('rotating', () => {
      repositionFabricTextbox(textObj, textboxContainer);
    });

    textboxContainer.on('moving', () => {
      repositionFabricTextbox(textObj, textboxContainer);
    });

    // a copied pair inherits the original's transient layout state; clear it so
    // the copy schedules its own font-ready refit and owns its own debug objects
    textObj.set({ hasPendingFontLoad: false, textLayoutDebugObjects: null });

    syncTextboxWithContainer();
  };

  const setZoom = (canvas, canvasOverlay, zoom, point = null) => {
    isZoomToFitRef.current = false;
    const clamped = clampZoom(zoom);
    const center = point || canvas.getCenter();
    canvas.zoomToPoint(new fabric.Point(center.left, center.top), clamped);
    canvasOverlay.zoomToPoint(
      new fabric.Point(center.left, center.top),
      clamped
    );
    canvasOverlay.requestRenderAll();
    canvas.requestRenderAll();
    setZoomLevel(clamped);
  };

  const zoomToFit = (canvas, canvasOverlay = null, skipSetState = false) => {
    if (!canvas) return;

    const allObjects = canvas.getObjects();
    if (!allObjects.length) return;

    const bounds = getBoundingBox(allObjects);
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const scaleX = canvasWidth / bounds.width;
    const scaleY = canvasHeight / bounds.height;
    const zoom = Math.min(scaleX, scaleY) * 0.9;

    const center = canvas.getCenter();
    canvas.zoomToPoint(new fabric.Point(center.left, center.top), zoom);
    if (canvasOverlay) {
      canvasOverlay.zoomToPoint(
        new fabric.Point(center.left, center.top),
        zoom
      );
    }

    const viewportX =
      (canvasWidth - bounds.width * zoom) / 2 - bounds.left * zoom;
    const viewportY =
      (canvasHeight - bounds.height * zoom) / 2 - bounds.top * zoom;

    const { viewportTransform } = canvas;
    viewportTransform[4] = viewportX;
    viewportTransform[5] = viewportY;
    canvas.requestRenderAll();

    if (canvasOverlay) {
      const { viewportTransform: overlayVpt } = canvasOverlay;
      overlayVpt[4] = viewportX;
      overlayVpt[5] = viewportY;
      canvasOverlay.requestRenderAll();
    }

    if (!skipSetState) {
      setZoomLevel('zoomToFit');
      isZoomToFitRef.current = true;
    }
  };

  const resizeCanvas = (
    canvasRef,
    fabricRef,
    fabricOverlayRef = null,
    skipSetState = false
  ) => {
    const parent = canvasRef.current.parentNode.parentNode;
    const width = parent.clientWidth;
    const height = parent.clientHeight;

    const canvasEl = canvasRef.current;
    canvasEl.width = width;
    canvasEl.height = height;

    if (fabricRef.current) {
      fabricRef.current.setWidth(width);
      fabricRef.current.setHeight(height);

      if (fabricOverlayRef) {
        fabricOverlayRef.current.setWidth(width);
        fabricOverlayRef.current.setHeight(height);
      }

      if (isZoomToFitRef.current) {
        zoomToFit(fabricRef.current, fabricOverlayRef.current, skipSetState);
      }
      else {
        fabricRef.current.requestRenderAll();

        if (fabricOverlayRef) {
          fabricOverlayRef.current.requestRenderAll();
        }
      }
    }
  };

  const exportCanvasAsHighDpiPNG = async (
    fabricCanvas,
    { backgroundGroup } = {},
    dpi = 300
  ) => {
    const bgGroup = backgroundGroup || backgroundGroupRef.current;
    if (!bgGroup) return null;

    const bounds = bgGroup.getBoundingRect(true);
    const scaleFactor = dpi / 96;

    const exportCanvas = new fabric.StaticCanvas(null, {
      backgroundColor: 'transparent',
      width: bounds.width,
      height: bounds.height,
      centeredRotation: true
    });

    // textbox containers are transparent Rects and are excluded on purpose
    const designObjects = fabricCanvas
      .getObjects()
      .filter(
        (obj) => (obj.type === 'image' || obj.type === 'textbox') && obj.visible
      );

    await resolveFontsAndRefitTextObjects(
      designObjects.filter((obj) => obj.type === 'textbox')
    );

    const clonePromises = designObjects.map(async (item) => {
      try {
        const clone = await item.clone();

        if (!clone) throw new Error('Clone failed');

        let clonedClipPath = null;
        if (item.clipPath) {
          clonedClipPath = await item.clipPath.clone();

          clonedClipPath.set({
            left: item.clipPath.left - item.left + (clone.left || 0),
            top: item.clipPath.top - item.top + (clone.top || 0),
            scaleX: item.clipPath.scaleX,
            scaleY: item.clipPath.scaleY,
            angle: item.clipPath.angle || 0,
            originX: item.clipPath.originX || 'center',
            originY: item.clipPath.originY || 'center'
          });

          clonedClipPath.setCoords();
        }

        clone.set({
          left: item.left - bounds.left,
          top: item.top - bounds.top,
          scaleX: item.scaleX,
          scaleY: item.scaleY,
          angle: item.angle,
          flipX: item.flipX,
          flipY: item.flipY,
          originX: item.originX,
          originY: item.originY
        });

        clone.setCoords();

        return clone;
      } catch (err) {
        console.warn('Skipping design item due to clone error:', err);
        return null;
      }
    });

    const clonedObjects = await Promise.all(clonePromises);

    clonedObjects.forEach((obj) => {
      if (obj) {
        exportCanvas.add(obj);
      }
    });

    exportCanvas.renderAll();

    const dataURL = exportCanvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: scaleFactor,
      enableRetinaScaling: false
    });

    return dataURL;
  };

  const loadSVGTemplate = async (
    canvas,
    canvasOverlay,
    { imageUrl, customId },
    onCallback = null,
    createTempReference = false
  ) => {
    try {
      let backgroundGroupTempRef = null;
      let clipPathTempRef = null;
      let outlineGroupTempRef = null;

      const { objects } = await fabric.loadSVGFromURL(imageUrl);
      const backgroundObjects = [];
      const outlineObjects = [];

      // split background vs outline by stroke or fill color
      for (const obj of objects) {
        const strokeColorInfo = getColorInfo(obj.stroke || obj.fill);
        const isBlackBorder =
          !strokeColorInfo.isTransparent &&
          strokeColorInfo.hex === '000000' &&
          strokeColorInfo.alpha === 1;

        if (isBlackBorder) {
          outlineObjects.push(obj);
        }
        else {
          const fillColorInfo = getColorInfo(obj.fill);
          const isWhiteBackground =
            !fillColorInfo.isTransparent && fillColorInfo.hex === 'ffffff';
          if (isWhiteBackground) {
            obj.set({ isBackgroundColor: true });
          }

          backgroundObjects.push(obj);
        }
      }

      const canvasSize = getCanvasSize(canvas);
      const centerLeft = canvasSize.width / 2;
      const centerTop = canvasSize.height / 2;

      // background group — bottom layer
      const bgGroup = new fabric.Group(backgroundObjects, {
        id: `backgroundGroup${customId || ''}`,
        left: centerLeft,
        top: centerTop,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        excludeFromExport: true,
        objectCaching: false,
        absolutePositioned: true
      });
      bgGroup.setCoords();
      bgGroup.width = Math.round(bgGroup.width);
      bgGroup.height = Math.round(bgGroup.height);

      if (createTempReference) {
        backgroundGroupTempRef = bgGroup;
      }
      else {
        backgroundGroupRef.current = bgGroup;
      }

      const clipObjects = backgroundObjects.map(createClipObject);

      // group the filled clip objects for the clipPath
      const clipPath = new fabric.Group(clipObjects, {
        id: `clipPath${customId || ''}`,
        left: centerLeft,
        top: centerTop,
        width: bgGroup.width,
        height: bgGroup.height,
        originX: 'center',
        originY: 'center',
        absolutePositioned: true,
        excludeFromExport: true,
        objectCaching: false,
        selectable: false,
        evented: false
      });
      clipPath.setCoords();

      if (createTempReference) {
        clipPathTempRef = clipPath;
      }
      else {
        clipPathRef.current = clipPath;
      }

      // outline group — top layer, visible border
      const outlineGroup = new fabric.Group(outlineObjects, {
        id: `outlineGroup${customId || ''}`,
        left: centerLeft,
        top: centerTop,
        width: bgGroup.width,
        height: bgGroup.height,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        excludeFromExport: true,
        objectCaching: false,
        absolutePositioned: true
      });
      outlineGroup.setCoords();
      if (createTempReference) {
        outlineGroupTempRef = outlineGroup;
      }
      else {
        outlineGroupRef.current = outlineGroup;
      }

      canvas.insertAt(0, bgGroup);
      canvas.renderAll();

      if (canvasOverlay) {
        // setup overlay canvas
        if (backgroundObjects[1]) {
          const dimOverlay = new fabric.Rect({
            left: 0,
            top: 0,
            width: canvas.getWidth(),
            height: canvas.getHeight(),
            fill: 'rgba(240, 240, 240, 0.4)',
            selectable: false,
            evented: false,
            absolutePositioned: true
          });

          const overlayClipObjects = [backgroundObjects[1]].map(
            createClipObject
          );
          const clipGroup = new fabric.Group(overlayClipObjects, {
            left: canvas.getWidth() / 2,
            top: canvas.getHeight() / 2,
            originX: 'center',
            originY: 'center',
            inverted: true,
            absolutePositioned: true,
            objectCaching: false
          });
          dimOverlay.set({ clipPath: clipGroup });

          canvasOverlay.insertAt(0, dimOverlay);
        }

        const overlayGuideLineClipObjects = [backgroundObjects[0]].map(
          createClipObject
        );
        const overlayGuideLineClipGroup = new fabric.Group(
          overlayGuideLineClipObjects,
          {
            left: canvas.getWidth() / 2,
            top: canvas.getHeight() / 2,
            originX: 'center',
            originY: 'center',
            absolutePositioned: true,
            objectCaching: false
          }
        );
        overlayGuideLineClipGroupRef.current = overlayGuideLineClipGroup;

        canvasOverlay.insertAt(1, outlineGroup);
        canvasOverlay.renderAll();
      }

      if (isZoomToFitRef.current) {
        zoomToFit(canvas, canvasOverlay, createTempReference);
      }

      if (onCallback) {
        onCallback({
          backgroundGroup: backgroundGroupTempRef || backgroundGroupRef.current,
          clipPath: clipPathTempRef || clipPathRef.current,
          outlineGroup: outlineGroupTempRef || outlineGroupRef.current
        });
      }
    } catch (error) {
      console.error('Error loading SVG:', error);
    }
  };

  const positionItemByOffset = (
    item,
    backgroundGroup,
    {
      leftPercent,
      scaleX,
      scaleY,
      topPercent,
      topOffset,
      bottomOffset,
      leftOffset,
      rightOffset,
      leftDifferenceFromScaling,
      topDifferenceFromScaling
    } = {}
  ) => {
    let offsetTopPercent = null;
    if (topOffset !== null) {
      offsetTopPercent =
        ((topOffset * FABRIC_CANVAS_DPI) / backgroundGroup.height) * 100;
    }
    else if (bottomOffset !== null) {
      offsetTopPercent =
        ((backgroundGroup.height -
          item.height * item.scaleY -
          bottomOffset * FABRIC_CANVAS_DPI) /
          backgroundGroup.height) *
        100;
    }

    let offsetLeftPercent = null;
    if (leftOffset !== null) {
      offsetLeftPercent =
        ((leftOffset * FABRIC_CANVAS_DPI) / backgroundGroup.width) * 100;
    }
    else if (rightOffset !== null) {
      offsetLeftPercent =
        ((backgroundGroup.width -
          item.width * item.scaleX -
          rightOffset * FABRIC_CANVAS_DPI) /
          backgroundGroup.width) *
        100;
    }

    if (offsetTopPercent !== null || offsetLeftPercent !== null) {
      const offsetItemPosition = calculateItemPosition(
        {
          leftPercent:
            offsetLeftPercent !== null ? offsetLeftPercent : leftPercent,
          topPercent: offsetTopPercent !== null ? offsetTopPercent : topPercent,
          scaleX,
          scaleY
        },
        item,
        backgroundGroup
      );

      item.set({
        left:
          offsetLeftPercent !== null
            ? offsetItemPosition.left
            : offsetItemPosition.left + leftDifferenceFromScaling,
        top:
          offsetTopPercent !== null
            ? offsetItemPosition.top
            : offsetItemPosition.top + topDifferenceFromScaling
      });
    }
  };

  const positionItem = (
    item,
    backgroundGroup,
    {
      leftPercent,
      topPercent,
      scaleX,
      scaleY,
      scale,
      angle,
      widthInInches,
      heightInInches,
      resizeWithinBounds,
      flipX,
      flipY,
      topOffset,
      bottomOffset,
      wideImageOffsetTop,
      leftOffset,
      rightOffset
    } = {}
  ) => {
    const isImage =
      item.fkEcomPrintDesignItemType === ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE;

    let leftDifferenceFromScaling = 0;
    let topDifferenceFromScaling = 0;

    let itemScaleX = scaleX;
    let itemScaleY = scaleY;

    // backwards compatibility for uniform scale
    if (scale !== undefined) {
      itemScaleX = scale;
      itemScaleY = scale;
    }

    // resize using inches
    if (heightInInches) {
      itemScaleY = (heightInInches / item.height) * UNIFORM_DPI;

      // images remain uniform
      if (isImage) {
        itemScaleX = itemScaleY;
      }
    }

    if (widthInInches) {
      itemScaleX = (widthInInches / item.width) * UNIFORM_DPI;

      // images remain uniform
      if (isImage) {
        itemScaleY = itemScaleX;
      }
    }

    let itemTopOffset = topOffset;

    // ensure image fits within bounds if specified
    if (
      resizeWithinBounds &&
      (itemScaleX !== item.scaleX || itemScaleY !== item.scaleY)
    ) {
      const maxScaleX = backgroundGroup.width / item.width;
      const maxScaleY = backgroundGroup.height / item.height;

      if (isImage) {
        const maxScale = Math.min(maxScaleX, maxScaleY, 2);

        itemScaleX = Math.min(
          itemScaleX,
          maxScale * (UNIFORM_DPI / FABRIC_CANVAS_DPI)
        );

        itemScaleY = itemScaleX;
      }
      else {
        itemScaleX = Math.min(
          itemScaleX,
          maxScaleX * (UNIFORM_DPI / FABRIC_CANVAS_DPI)
        );

        itemScaleY = Math.min(
          itemScaleY,
          maxScaleY * (UNIFORM_DPI / FABRIC_CANVAS_DPI)
        );
      }

      if (isImage && wideImageOffsetTop && heightInInches) {
        const oldImageHeight =
          item.height *
          ((heightInInches / item.height) * UNIFORM_DPI) *
          (FABRIC_CANVAS_DPI / UNIFORM_DPI);

        const resizedImageHeight =
          item.height * itemScaleY * (FABRIC_CANVAS_DPI / UNIFORM_DPI);

        // height difference exceeds offset difference
        if (
          (oldImageHeight - resizedImageHeight) / 100 >
          wideImageOffsetTop - topOffset
        ) {
          itemTopOffset = wideImageOffsetTop;
        }
      }
    }

    const reverseScaleFactor = FABRIC_CANVAS_DPI / UNIFORM_DPI;

    if (itemScaleX !== undefined && itemScaleX !== item.scaleX) {
      leftDifferenceFromScaling =
        (item.scaleX - itemScaleX * reverseScaleFactor) * (item.width / 2);
    }

    if (itemScaleY !== undefined && itemScaleY !== item.scaleY) {
      topDifferenceFromScaling =
        (item.scaleY - itemScaleY * reverseScaleFactor) * (item.height / 2);
    }

    const itemPosition = calculateItemPosition(
      {
        leftPercent,
        topPercent,
        scaleX: itemScaleX,
        scaleY: itemScaleY
      },
      item,
      backgroundGroup
    );

    const {
      topOffset: currentTopOffset,
      bottomOffset: currentBottomOffset,
      leftOffset: currentLeftOffset,
      rightOffset: currentRightOffset
    } = calculatePercentPosition(item, backgroundGroup);

    const isTopDiff = isPositionDifferent(currentTopOffset, itemTopOffset);
    const isBottomDiff = isPositionDifferent(currentBottomOffset, bottomOffset);
    const isLeftDiff = isPositionDifferent(currentLeftOffset, leftOffset);
    const isRightDiff = isPositionDifferent(currentRightOffset, rightOffset);

    item.set({
      ...itemPosition,
      angle: item.angle,
      flipX: flipX ?? item.flipX,
      flipY: flipY ?? item.flipY,
      scaleX:
        itemScaleX !== undefined
          ? itemScaleX * reverseScaleFactor
          : item.scaleX,
      scaleY:
        itemScaleY !== undefined
          ? itemScaleY * reverseScaleFactor
          : item.scaleY,
      left: itemPosition.left + leftDifferenceFromScaling,
      top: itemPosition.top + topDifferenceFromScaling
    });

    positionItemByOffset(item, backgroundGroup, {
      leftPercent,
      topPercent,
      scaleX: itemScaleX,
      scaleY: itemScaleY,
      topOffset: isTopDiff ? itemTopOffset : null,
      bottomOffset: isBottomDiff ? bottomOffset : null,
      leftOffset: isLeftDiff ? leftOffset : null,
      rightOffset: isRightDiff ? rightOffset : null,
      leftDifferenceFromScaling,
      topDifferenceFromScaling
    });

    if (angle !== undefined) {
      item.rotate(angle);
    }

    item.setCoords();
  };

  const addPersonalizedTextbox = (
    canvas,
    text,
    textProperties,
    { outlineGroup, backgroundGroup, clipPath } = {},
    defaultPositioningConfig = null
  ) => {
    const oGroup = outlineGroup || outlineGroupRef.current;
    oGroup?.setCoords();
    const clipBounds = oGroup?.getBoundingRect();

    const bgGroup = backgroundGroup ?? backgroundGroupRef.current;
    if (!bgGroup) {
      console.warn(
        'No background group found; unable to add personalized text.'
      );
      return null;
    }

    const wasRestoring = isRestoring.current;
    if (!textProperties) {
      isRestoring.current = true;
    }

    const idKey = createDesignItemId('text');
    const itemId = textProperties?.id || idKey;

    const centerX = clipBounds ? bgGroup.left : canvas.getWidth() / 2;
    const centerY = clipBounds ? bgGroup.top : canvas.getHeight() / 2;

    const defaultScaleX = (bgGroup.width / DEFAULT_TEXTBOX_SIZE) * 0.9;
    const defaultScaleY = (bgGroup.height / DEFAULT_TEXTBOX_SIZE) * 0.2;

    const textboxContainer = new fabric.Rect({
      width: DEFAULT_TEXTBOX_SIZE,
      height: DEFAULT_TEXTBOX_SIZE,
      originX: 'center',
      originY: 'center',
      left: centerX,
      top: centerY,
      visible: true,
      scaleX: defaultScaleX,
      scaleY: defaultScaleY,
      ...pickDefinedProps(textProperties, CONTAINER_GEOMETRY_PROPS),
      id: textProperties?.containerId || `${itemId}-container`,
      fill: 'transparent',
      stroke: 'transparent',
      lockScalingFlip: true,
      hasControls: true,
      hasBorders: true,
      selectable: true,
      borderColor: '#00e0ff',
      transparentCorners: false,
      clipPath: clipPath ?? clipPathRef.current,
      fkEcomPrintDesignItemType:
        ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT
    });

    // default positioning if no properties provided
    if (!textProperties && defaultPositioningConfig) {
      const percentPosition = calculatePercentPosition(
        textboxContainer,
        bgGroup
      );
      positionItem(textboxContainer, bgGroup, {
        ...percentPosition,
        ...({
          ...defaultPositioningConfig,
          resizeWithinBounds: true
        } || {
          topOffset: percentPosition.topOffset,
          bottomOffset: percentPosition.bottomOffset,
          leftOffset: percentPosition.leftOffset,
          rightOffset: percentPosition.rightOffset,
          widthInInches: null,
          heightInInches: null
        })
      });
    }

    // fabric needs a starting size to lay the text out; the container sync
    // immediately refits it, and the saved value is the reference the UI reads
    const referenceFontSize =
      Number(
        textProperties?.fontSize ?? textProperties?.itemProperties?.fontSize
      ) || DEFAULT_FONT_SIZE;

    const defaultItemProperties = {
      characterLimit: 256,
      fontColor: '#000000',
      allowMultiline: true,
      fitMode: FIT_MODE.INK,
      safetyInsetPercent: DEFAULT_SAFETY_INSET_PERCENT,
      isUserInput: true,
      isRequired: true,
      noLetters: false,
      noNumbers: false,
      noSpecialCharacters: false,
      noSpaces: false,
      inputLabel: 'Custom text',
      ...textProperties?.itemProperties,
      fontSize: referenceFontSize
    };

    const textboxText = textProperties
      ? text || ''
      : sanitizeText(DEFAULT_TEXTBOX_TEXT, {
          removeLetters: defaultItemProperties.noLetters,
          removeNumbers: defaultItemProperties.noNumbers,
          removeSpecialCharacters: defaultItemProperties.noSpecialCharacters,
          removeSpaces: defaultItemProperties.noSpaces,
          characterLimit: defaultItemProperties.characterLimit,
          textTransform: defaultItemProperties.textTransform
        }) || DEFAULT_TEXTBOX_TEXT;

    const textObj = new fabric.Textbox(textboxText, {
      id: itemId,
      pkDesignItem: textProperties?.pkDesignItem || null,
      containerId: textboxContainer.id,
      originX: 'center',
      originY: 'center',
      left: textboxContainer.left,
      top: textboxContainer.top,
      width: textboxContainer.width,
      visible: textboxContainer.visible,
      fontSize: referenceFontSize,
      fill: defaultItemProperties.fontColor,
      fontFamily:
        textProperties?.itemProperties?.fontFamily ??
        textProperties?.fontFamily ??
        'ClassicVarsity',
      textAlign:
        textProperties?.itemProperties?.textAlign ??
        textProperties?.textAlign ??
        'center',
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      clipPath: clipPath ?? clipPathRef.current,
      itemProperties: defaultItemProperties,
      fkEcomPrintDesignItemType:
        ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT
    });

    setupTextboxContainerSync(textObj, textboxContainer);
    canvas.add(textboxContainer);

    if (textboxContainer.visible && !textProperties) {
      canvas.setActiveObject(textboxContainer);
    }
    if (!textProperties) {
      isRestoring.current = wasRestoring;
    }

    canvas.add(textObj);
    canvas.requestRenderAll();

    return { textObj, container: textboxContainer };
  };

  const addImageByUrl = (
    canvas,
    url,
    imageProperties,
    { outlineGroup, backgroundGroup, clipPath } = {},
    defaultPositioningConfig = null
  ) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';

      image.onload = () => {
        const img = new fabric.Image(image);

        const oGroup = outlineGroup || outlineGroupRef.current;
        if (!oGroup) {
          console.warn('No outline group found; adding image without fitting.');
        }

        // recompute outline bounds
        oGroup.setCoords();
        const clipBounds = oGroup.getBoundingRect();

        // defensive fallback if no bounds
        const maxWidth = clipBounds?.width ?? canvas.getWidth();
        const maxHeight = clipBounds?.height || canvas.getHeight();

        // calculate scale to fit image within the outline bounds
        const scaleX = maxWidth / image.width;
        const scaleY = maxHeight / image.height;
        const scale = Math.min(scaleX, scaleY, 2);

        const bgGroup = backgroundGroup ?? backgroundGroupRef.current;
        const centerX = clipBounds ? bgGroup.left : canvas.getWidth() / 2;
        const centerY = clipBounds ? bgGroup.top : canvas.getHeight() / 2;

        img.set({
          id: createDesignItemId('image'),
          pkDesignItem: imageProperties?.pkDesignItem || null,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
          ...imageProperties,
          left: imageProperties
            ? imageProperties.left + (img.width * imageProperties.scaleX) / 2
            : centerX,
          top: imageProperties
            ? imageProperties.top + (img.height * imageProperties.scaleY) / 2
            : centerY,
          hasControls: true,
          hasBorders: true,
          selectable: true,
          lockUniScaling: true,
          clipPath: clipPath ?? clipPathRef.current,
          borderColor: '#00e0ff',
          transparentCorners: false,
          fkEcomPrintDesignItemType: ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE
        });

        // default image positioning if no properties provided
        if (!imageProperties && defaultPositioningConfig) {
          const percentPosition = calculatePercentPosition(img, bgGroup);
          positionItem(img, bgGroup, {
            ...percentPosition,
            ...({
              ...defaultPositioningConfig,
              resizeWithinBounds: true
            } || {
              topOffset: percentPosition.topOffset,
              bottomOffset: percentPosition.bottomOffset,
              leftOffset: percentPosition.leftOffset,
              rightOffset: percentPosition.rightOffset,
              widthInInches: null,
              heightInInches: null
            })
          });
        }

        // Disable the side scaling controls (top, bottom, left, right)
        img.setControlVisible('mt', false);
        img.setControlVisible('mb', false);
        img.setControlVisible('ml', false);
        img.setControlVisible('mr', false);

        canvas.add(img);
        if (img.visible && !imageProperties) {
          canvas.setActiveObject(img);
        }

        canvas.requestRenderAll();

        resolve(img);
      };

      image.onerror = (err) => {
        console.error(`Failed to load image:  ${image.src}`, err);
        if (image.src === url) {
          reject(err);
        }
        else {
          console.warn(`Retrying image load: ${url}`);
          image.src = url;
        }
      };

      if (url.includes(S3_PUBLIC_URL)) {
        image.src = url.replace(S3_PUBLIC_URL, S3_PUBLIC_URL_OLD);
      }
      else {
        image.src = url.replace(S3_PUBLIC_URL_OLD, S3_PUBLIC_URL);
      }
    });

  const changeBackgroundColor = (canvas, color, { backgroundGroup } = {}) => {
    const bgGroup = backgroundGroup || backgroundGroupRef.current;

    if (!canvas || !bgGroup) {
      return;
    }

    bgGroup.getObjects().forEach((obj) => {
      if (obj.isBackgroundColor) {
        obj.set({ fill: color || '#FFF' });
        obj.setCoords();
      }
    });

    canvas.requestRenderAll();
  };

  const getCanvasSaveState = (canvas, { backgroundGroup } = {}) => {
    if (!canvas) return { items: [] };

    const bgGroup = backgroundGroup || backgroundGroupRef.current;

    const allCanvasObjects = canvas.getObjects();
    const items = allCanvasObjects
      .filter((obj) => obj.type === 'image' || obj.type === 'textbox')
      .map((obj, index) => {
        const item = obj.toObject(['id', 'src', 'pkDesignItem', 'containerId']);

        let itemContainer = null;
        if (item.containerId) {
          itemContainer = allCanvasObjects.find(
            (c) => c.id === item.containerId
          );
        }

        const {
          leftPercent,
          topPercent,
          scaleX,
          scaleY,
          topOffset,
          bottomOffset,
          leftOffset,
          rightOffset
        } = calculatePercentPosition(itemContainer || item, bgGroup);

        const saveProps = itemContainer || item;

        // the layout the editor resolved travels to the server-side renderer as
        // an item-property row - the only text channel the print pipeline
        // persists. A freshly laid-out object wins; otherwise the row loaded
        // from the save state is carried through unchanged
        const resolvedLayoutValue = obj.resolvedLayout
          ? JSON.stringify(obj.resolvedLayout)
          : typeof obj.itemProperties?.resolvedLayout === 'string'
          ? obj.itemProperties.resolvedLayout
          : null;

        // the persisted rows must describe what the canvas actually shows, so
        // the live fabric values override whatever itemProperties last held -
        // a fill changed outside setItemProperties would otherwise save stale
        const mergedItemProperties = obj.itemProperties
          ? Object.fromEntries(
              Object.entries({
                ...obj.itemProperties,
                fontSize: item.fontSize,
                fontFamily: item.fontFamily,
                textAlign: item.textAlign,
                fontColor: item.fill
              }).filter(([, value]) => value !== undefined)
            )
          : null;

        return {
          id: item.id,
          containerId: item.containerId,
          pkDesignItem: item.pkDesignItem || null,
          sort: index + 1,
          src: item.src,
          fkEcomPrintDesignItemType: obj.fkEcomPrintDesignItemType,
          type: ECOM_PRINT_DESIGN_ITEM_TYPE_LIST.find(
            (type) => type.value === obj.fkEcomPrintDesignItemType
          ).stringValue,
          text: item.text,
          visible: item.visible,
          scaleX,
          scaleY,
          opacity: item.opacity,
          leftPercent,
          topPercent,
          angle: saveProps.angle,
          width: saveProps.width,
          height: saveProps.height,
          currentWidth: saveProps.width * saveProps.scaleX,
          currentHeight: saveProps.height * saveProps.scaleY,
          currentHeightInInches:
            (saveProps.height * saveProps.scaleY) / FABRIC_CANVAS_DPI,
          currentWidthInInches:
            (saveProps.width * saveProps.scaleX) / FABRIC_CANVAS_DPI,
          flipX: item.flipX,
          flipY: item.flipY,
          topOffset,
          bottomOffset,
          leftOffset,
          rightOffset,
          fontSize: item.fontSize,
          fontFamily: item.fontFamily,
          textAlign: item.textAlign,
          // output for the server-side renderer only; the editor always
          // recalculates it on load and never treats it as authoritative
          resolvedLayout: obj.resolvedLayout || null,
          itemPropertiesList: [
            ...getDesignItemPropertiesListFromObject(
              mergedItemProperties
            ).filter(({ name }) => name !== 'resolvedLayout'),
            ...(resolvedLayoutValue
              ? [{ name: 'resolvedLayout', value: resolvedLayoutValue }]
              : [])
          ],
          itemProperties: mergedItemProperties
        };
      });

    return { items };
  };

  const saveCanvasState = (setHistoryState, canvas) => {
    if (isRestoring.current) return;
    isMovingObject.current = false;

    const canvasState = getCanvasSaveState(canvas);

    setHistoryState((prev) => {
      const truncatedHistory =
        prev.history?.slice(0, prev.currentHistoryIndex + 1) || [];
      const newHistory = [...truncatedHistory, canvasState].slice(-MAX_HISTORY);

      const id =
        prev.id || (prev.hasInitialized ? `${new Date().getTime()}` : null);

      return {
        ...prev,
        id,
        history: newHistory,
        currentHistoryIndex: newHistory.length - 1,
        hasInitialized: true,
        hasChanges: newHistory.length > 1
      };
    });
  };

  const reorderCanvasObjectsBySort = (canvas, setHistoryState, layerData) => {
    isRestoring.current = true;
    const sorted = layerData.slice().sort((a, b) => a.sort - b.sort);
    const canvasObjects = canvas.getObjects();

    const objectsToReorder = sorted
      .map(({ id }) => canvasObjects.find((obj) => obj.id === id))
      .filter(Boolean);

    objectsToReorder.forEach((obj) => canvas.remove(obj));
    objectsToReorder.forEach((obj) => canvas.add(obj));

    canvas.requestRenderAll();

    if (setHistoryState) {
      setTimeout(() => {
        isRestoring.current = false;
        saveCanvasState(setHistoryState, canvas);
      }, 0);
    }
  };

  const loadCanvasContentFromJSON = (
    canvas,
    canvasState,
    { oldCanvasWidth, oldCanvasHeight } = {},
    backgroundGroup = null,
    onSuccessCallback = null
  ) => {
    const canvasItems = canvasState.items.sort((a, b) => a.sort - b.sort);

    if (canvasItems.length > 0) {
      const wrappedPromises = canvasItems.map((item) => {
        const positioning = calculateItemPosition(
          item,
          null,
          backgroundGroup ?? backgroundGroupRef.current,
          {
            oldCanvasWidth,
            oldCanvasHeight
          }
        );

        const itemProperties = Array.isArray(item.itemPropertiesList)
          ? getDesignItemPropertiesObjectFromList(item.itemPropertiesList)
          : typeof item.itemProperties === 'object'
          ? item.itemProperties
          : null;

        const itemData = {
          ...item,
          ...positioning,
          itemProperties
        };

        if (
          item.fkEcomPrintDesignItemType ===
          ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE
        ) {
          return addImageByUrl(canvas, item.src, itemData)
            .then((result) => ({ status: 'fulfilled', value: result }))
            .catch((error) => ({ status: 'rejected', reason: error }));
        }

        if (
          item.fkEcomPrintDesignItemType ===
          ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT
        ) {
          addPersonalizedTextbox(canvas, item.text || item.src, itemData);
          return null;
        }

        return null;
      });

      Promise.all(wrappedPromises).then(() => {
        reorderCanvasObjectsBySort(canvas, null, canvasItems);
        isRestoring.current = false;
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      });
    }
    else {
      isRestoring.current = false;
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    }
  };

  const loadCanvasTemplateState = (
    canvas,
    canvasOverlay,
    setHistoryState,
    { templateImage, canvasState, printImageUrl },
    onSuccessCallback = null,
    createTempReference = false
  ) => {
    isRestoring.current = true;

    canvas.getObjects().forEach((obj) => canvas.remove(obj));
    if (canvasOverlay) {
      isZoomToFitRef.current = true;
      canvasOverlay.getObjects().forEach((obj) => canvasOverlay.remove(obj));
    }

    const onSuccess = ({ clipPath, backgroundGroup, outlineGroup } = {}) => {
      isRestoring.current = false;
      if (setHistoryState) {
        saveCanvasState(setHistoryState, canvas);
      }

      if (onSuccessCallback) {
        onSuccessCallback({ clipPath, backgroundGroup, outlineGroup });
      }
    };

    if (templateImage) {
      loadSVGTemplate(
        canvas,
        canvasOverlay,
        { imageUrl: templateImage },
        ({ clipPath, backgroundGroup, outlineGroup }) => {
          if (printImageUrl) {
            const image = new Image();

            image.onload = () => {
              const scaleFactor = FABRIC_CANVAS_DPI / UNIFORM_DPI;

              const oldCanvasWidth = Math.round(image.width * scaleFactor);
              const oldCanvasHeight = Math.round(image.height * scaleFactor);

              if (canvasState) {
                loadCanvasContentFromJSON(
                  canvas,
                  canvasState,
                  {
                    oldCanvasWidth,
                    oldCanvasHeight
                  },
                  backgroundGroup,
                  () => onSuccess({ clipPath, backgroundGroup, outlineGroup })
                );
              }
              else {
                onSuccess({ clipPath, backgroundGroup, outlineGroup });
              }
            };

            image.src = printImageUrl;
          }
          else if (canvasState) {
            loadCanvasContentFromJSON(
              canvas,
              canvasState,
              {},
              backgroundGroup,
              () => onSuccess({ clipPath, backgroundGroup, outlineGroup })
            );
          }
          else {
            onSuccess({ clipPath, backgroundGroup, outlineGroup });
          }
        },
        createTempReference
      );
    }
    else if (canvasState) {
      loadCanvasContentFromJSON(canvas, canvasState, {}, null, onSuccess);
    }
    else {
      onSuccess();
    }
  };

  const restoreHistory = (prev, canvas, targetIndex) => {
    if (targetIndex < 0 || targetIndex >= prev.history.length) {
      return prev;
    }

    isRestoring.current = true;

    const saveState = prev.history[targetIndex];

    // update existing images to match history or hide if not in history
    const canvasObjects = canvas.getObjects();
    canvasObjects.forEach((obj) => {
      if (obj.type !== 'image' && obj.type !== 'textbox') {
        return;
      }

      const hist = saveState.items.find((i) => i.id === obj.id);

      const histContainer = obj.containerId
        ? canvasObjects.find((i) => i.id === obj.containerId)
        : null;
      const positioningItem = histContainer || obj;
      if (hist && positioningItem) {
        positioningItem.set({
          visible: hist.visible,
          opacity: hist.opacity,
          flipX: hist.flipX,
          flipY: hist.flipY,
          originX: 'center',
          originY: 'center',
          clipPath: clipPathRef.current,
          itemProperties: hist.itemProperties,
          ...calculateItemPosition(hist, obj, backgroundGroupRef.current)
        });
        positioningItem.setCoords();

        if (
          obj.fkEcomPrintDesignItemType ===
          ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT
        ) {
          obj.set({
            visible: hist.visible,
            text: hist.text,
            fill: hist.itemProperties?.fontColor ?? obj.fill,
            fontFamily: hist.fontFamily,
            textAlign: hist.textAlign,
            itemProperties: hist.itemProperties,
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
            scaleX: 1,
            scaleY: 1
          });
          obj.fire('modified');
        }
      }
      else {
        obj.set({ visible: false });
        if (obj.containerId) {
          canvasObjects
            .find((i) => i.id === obj.containerId)
            ?.set({ visible: false });
        }
      }
    });

    canvas.discardActiveObject();
    reorderCanvasObjectsBySort(canvas, null, saveState.items);

    setTimeout(() => {
      isRestoring.current = false;
    }, 0);

    return {
      ...prev,
      currentHistoryIndex: targetIndex
    };
  };

  const undo = (setHistoryState, canvas) => {
    setHistoryState((prev) => {
      const newIndex = prev.currentHistoryIndex - 1;
      if (newIndex < 0) return prev;
      return restoreHistory(prev, canvas, newIndex);
    });
  };

  const redo = (setHistoryState, canvas) => {
    setHistoryState((prev) => {
      const newIndex = prev.currentHistoryIndex + 1;
      if (newIndex >= prev.history.length) return prev;
      return restoreHistory(prev, canvas, newIndex);
    });
  };

  const deleteSelected = (setHistoryState, canvas) => {
    const activeObjects = canvas.getActiveObjects();
    const canvasObjects = canvas.getObjects();
    if (activeObjects.length) {
      activeObjects.forEach((obj) => {
        obj.set({ visible: false });

        if (obj.containerId) {
          canvasObjects
            .filter((o) => o.id === obj.containerId)
            .forEach((childObj) => {
              childObj.set({ visible: false });
            });
        }
        else {
          canvasObjects
            .filter((o) => o.containerId === obj.id)
            .forEach((childObj) => {
              childObj.set({ visible: false });
            });
        }
      });

      canvas.discardActiveObject();
      canvas.requestRenderAll();
      saveCanvasState(setHistoryState, canvas);
    }
  };

  const deleteObjectById = (setHistoryState, canvas, id) => {
    const canvasObjects = canvas.getObjects();
    const objectToDelete = canvasObjects.find((obj) => obj.id === id);
    if (objectToDelete) {
      objectToDelete.set({ visible: false });
      if (objectToDelete.containerId) {
        canvasObjects
          .filter((obj) => obj.id === objectToDelete.containerId)
          .forEach((childObj) => {
            childObj.set({ visible: false });
          });
      }
      else {
        canvasObjects
          .filter((obj) => obj.containerId === id)
          .forEach((childObj) => {
            childObj.set({ visible: false });
          });
      }

      canvas.discardActiveObject();
      canvas.requestRenderAll();
      saveCanvasState(setHistoryState, canvas);
    }
  };

  const createAndPopulateHiddenCanvasState = (
    config,
    includePrintImage = false
  ) =>
    new Promise((resolve, reject) => {
      const hiddenCanvas = document.createElement('canvas');
      hiddenCanvas.style.display = 'none';
      hiddenCanvas.id = new Date().getTime().toString();
      const canvas = new fabric.Canvas(hiddenCanvas, {
        backgroundColor: '#F0F0F0',
        retinaScaling: true,
        stopContextMenu: true,
        centeredRotation: true
      });

      const onResolve = (
        c,
        { clipPath, backgroundGroup, outlineGroup } = {}
      ) => {
        resolveFontsAndRefitTextObjects(
          c.getObjects().filter((obj) => obj.type === 'textbox')
        )
          .then(() => {
            const canvasState = getCanvasSaveState(c, { backgroundGroup });

            if (!includePrintImage) {
              hiddenCanvas.remove();
              resolve({ canvasState });
              return null;
            }

            return exportCanvasAsHighDpiPNG(c, { backgroundGroup }).then(
              (imageUrl) => {
                hiddenCanvas.remove();
                resolve({
                  canvasState,
                  imageUrl
                });
              }
            );
          })
          .catch(reject);
      };

      if (config.canvasState) {
        loadCanvasTemplateState(
          canvas,
          null,
          null,
          config,
          ({ clipPath, backgroundGroup, outlineGroup }) => {
            onResolve(canvas, {
              clipPath,
              backgroundGroup,
              outlineGroup
            });
          },
          true
        );
      }
      else if (config.imageUrl) {
        loadSVGTemplate(
          canvas,
          null,
          { imageUrl: config.templateImage, customId: hiddenCanvas.id },
          ({ clipPath, backgroundGroup, outlineGroup }) => {
            addImageByUrl(
              canvas,
              config.imageUrl,
              null,
              {
                clipPath,
                backgroundGroup,
                outlineGroup
              },
              config.defaultPositioning
            )
              .then(() => {
                onResolve(canvas, { clipPath, backgroundGroup, outlineGroup });
              })
              .catch(reject);
          },
          true
        );
      }
      else {
        onResolve(canvas);
      }
    });

  const selectObjectById = (canvas, id) => {
    const objectToSelect = canvas.getObjects().find((obj) => obj.id === id);

    if (objectToSelect) {
      canvas.setActiveObject(objectToSelect);
      canvas.requestRenderAll(); // Refreshes the canvas to show the selection
    }
    else {
      console.warn(`No object found with id: ${id}`);
    }
  };

  const setItemPosition = (
    setHistoryState,
    canvas,
    id,
    {
      leftPercent,
      topPercent,
      scaleX,
      scaleY,
      scale,
      angle,
      widthInInches,
      heightInInches,
      flipX,
      flipY,
      topOffset,
      bottomOffset,
      leftOffset,
      rightOffset
    } = {}
  ) => {
    const item = canvas.getObjects().find((obj) => obj.id === id);
    if (item) {
      let container = null;
      if (item.containerId) {
        container = canvas
          .getObjects()
          .find((obj) => obj.id === item.containerId);
      }

      positionItem(container || item, backgroundGroupRef.current, {
        leftPercent,
        topPercent,
        scaleX,
        scaleY,
        scale,
        angle,
        widthInInches,
        heightInInches,
        flipX,
        flipY,
        topOffset,
        bottomOffset,
        leftOffset,
        rightOffset
      });

      onTrackObjectPosition(container || item);
      saveCanvasState(setHistoryState, canvas);

      (container || item).setCoords();
      (container || item).fire('modified');
      canvas.requestRenderAll();
    }
  };

  const setItemProperties = (
    setHistoryState,
    canvas,
    id,
    {
      text,
      fontSize,
      fontFamily,
      itemProperties: {
        characterLimit,
        allowMultiline,
        fitMode,
        safetyInsetPercent,
        isUserInput,
        isRequired,
        noLetters,
        noNumbers,
        noSpecialCharacters,
        noSpaces,
        fontColor,
        inputLabel,
        textTransform
      } = {}
    } = {},
    skipHistory = false
  ) => {
    const item = canvas.getObjects().find((obj) => obj.id === id);
    if (item) {
      let itemProperties = {
        characterLimit: characterLimit ?? item.itemProperties.characterLimit,
        allowMultiline: allowMultiline ?? item.itemProperties.allowMultiline,
        fitMode: fitMode ?? item.itemProperties.fitMode,
        safetyInsetPercent:
          safetyInsetPercent ?? item.itemProperties.safetyInsetPercent,
        isUserInput: isUserInput ?? item.itemProperties.isUserInput,
        isRequired: isRequired ?? item.itemProperties.isRequired,
        noLetters: noLetters ?? item.itemProperties.noLetters,
        noNumbers: noNumbers ?? item.itemProperties.noNumbers,
        noSpecialCharacters:
          noSpecialCharacters ?? item.itemProperties.noSpecialCharacters,
        noSpaces: noSpaces ?? item.itemProperties.noSpaces,
        inputLabel: inputLabel ?? item.itemProperties.inputLabel,
        fontColor: fontColor ?? item.fill,
        fontFamily: fontFamily ?? item.fontFamily,
        fontSize: fontSize ?? item.fontSize,
        textTransform: textTransform ?? item.itemProperties.textTransform
      };

      itemProperties = Object.fromEntries(
        Object.entries(itemProperties).filter(
          ([_, value]) => value !== undefined
        )
      );

      const itemText = text ?? item.text;
      const sanitizedText = sanitizeText(itemText, {
        removeLetters: itemProperties.noLetters,
        removeNumbers: itemProperties.noNumbers,
        removeSpecialCharacters: itemProperties.noSpecialCharacters,
        removeSpaces: itemProperties.noSpaces,
        textTransform: itemProperties.textTransform
      });

      item.set({
        text: sanitizedText,
        fontSize: itemProperties.fontSize,
        fontFamily: itemProperties.fontFamily,
        fill: itemProperties.fontColor,
        itemProperties
      });

      onTrackObjectPosition(item);

      if (!skipHistory && (text !== undefined || text !== item.text)) {
        saveCanvasState(setHistoryState, canvas);
      }

      item.setCoords();
      item.fire('modified');
      canvas.requestRenderAll();
    }
  };

  const cloneItem = (item, idKey, clonedContainer, canvas) => {
    item.clone().then((clonedObject) => {
      clonedObject.set({
        ...item,
        id: `copy-${idKey}`,
        containerId: clonedContainer ? clonedContainer.id : null,
        pkDesignItem: null,
        itemProperties: item.itemProperties
          ? { ...item.itemProperties }
          : item.itemProperties,
        top: clonedObject.top + 10,
        left: clonedObject.left + 10
      });

      if (clonedContainer) {
        setupTextboxContainerSync(clonedObject, clonedContainer);
      }

      canvas.setActiveObject(clonedContainer || clonedObject);
      canvas.add(clonedObject);
      canvas.requestRenderAll();
    });
  };

  const copyObjectById = (canvas, id) => {
    const item = canvas.getObjects().find((obj) => obj.id === id);
    if (item) {
      const idKey = createDesignItemId('copy');
      let clonedContainer = null;

      const container = item.containerId
        ? canvas.getObjects().find((obj) => obj.id === item.containerId)
        : null;
      if (container) {
        container.clone().then((clonedObject) => {
          clonedObject.set({
            ...container,
            id: `copy-${idKey}-container`,
            containerId: null,
            top: clonedObject.top + 10,
            left: clonedObject.left + 10
          });
          clonedContainer = clonedObject;

          canvas.add(clonedObject);

          cloneItem(item, idKey, clonedObject, canvas);
        });
      }
      else {
        cloneItem(item, idKey, clonedContainer, canvas);
      }
    }
  };

  const fitObjectToTemplateById = (
    setHistoryState,
    canvas,
    id,
    mode,
    withPadding
  ) => {
    const item = canvas.getObjects().find((obj) => obj.id === id);
    if (!item) return;

    outlineGroupRef.current.setCoords();
    const clipBounds = outlineGroupRef.current.getBoundingRect();

    const isImage =
      item.fkEcomPrintDesignItemType === ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE;

    const padding = withPadding ? 25 : 0;
    const maxWidth = (clipBounds?.width ?? canvas.getWidth()) - padding * 2;
    const maxHeight = (clipBounds?.height ?? canvas.getHeight()) - padding * 2;

    const scaleX = maxWidth / item.width;
    const scaleY = maxHeight / item.height;

    let finalScaleX;
    let finalScaleY;

    if (isImage) {
      const uniformScale =
        mode === 'fill' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);

      finalScaleX = uniformScale;
      finalScaleY = uniformScale;
    }
    else {
      finalScaleX = mode === 'fill' ? scaleX : Math.min(scaleX, scaleY);
      finalScaleY = mode === 'fill' ? scaleY : Math.min(scaleX, scaleY);
    }

    const bgGroup = backgroundGroupRef.current;
    const centerX = clipBounds ? bgGroup.left : canvas.getWidth() / 2;
    const centerY = clipBounds ? bgGroup.top : canvas.getHeight() / 2;

    item.set({
      left: centerX,
      top: centerY,
      scaleX: finalScaleX,
      scaleY: finalScaleY
    });

    item.setCoords();
    item.fire('modified');
    canvas.requestRenderAll();

    saveCanvasState(setHistoryState, canvas);
  };

  const positionObjectToTemplateById = (
    setHistoryState,
    canvas,
    id,
    position,
    defaultPositioning
  ) => {
    const item = canvas.getObjects().find((obj) => obj.id === id);
    if (item) {
      const bgGroup = backgroundGroupRef.current;

      let data = {};
      switch (position?.toLowerCase()) {
        case 'horizontal': {
          data = { left: bgGroup.left };
          break;
        }
        case 'vertical': {
          data = { top: bgGroup.top };
          break;
        }
        case 'left': {
          data = {
            left:
              bgGroup.left - bgGroup.width / 2 + (item.width / 2) * item.scaleX
          };
          break;
        }
        case 'right': {
          data = {
            left:
              bgGroup.left + bgGroup.width / 2 - (item.width / 2) * item.scaleX
          };
          break;
        }
        case 'top': {
          data = {
            top:
              bgGroup.top - bgGroup.height / 2 + (item.height / 2) * item.scaleY
          };
          break;
        }
        case 'bottom': {
          data = {
            top:
              bgGroup.top + bgGroup.height / 2 - (item.height / 2) * item.scaleY
          };
          break;
        }
        case 'default': {
          data = {
            top: bgGroup.top,
            left: bgGroup.left
          };
          break;
        }
        default:
          break;
      }

      item.set(data);

      if (position === 'default' && defaultPositioning) {
        const percentPosition = calculatePercentPosition(item, bgGroup);

        positionItem(item, bgGroup, {
          ...percentPosition,
          ...({
            ...defaultPositioning,
            resizeWithinBounds: true
          } || {
            topOffset: percentPosition.topOffset,
            bottomOffset: percentPosition.bottomOffset,
            leftOffset: percentPosition.leftOffset,
            rightOffset: percentPosition.rightOffset,
            widthInInches: null,
            heightInInches: null
          })
        });
      }

      item.setCoords();
      item.fire('modified');
      canvas.requestRenderAll();

      saveCanvasState(setHistoryState, canvas);
    }
  };

  return {
    selectedObjectPosition,
    zoomLevel,
    setZoom,
    zoomToFit,
    loadSVGTemplate,
    setupGuides,
    setupPanAndZoom,
    setupObjectTracking,
    resizeCanvas,
    addPersonalizedTextbox,
    addImageByUrl,
    changeBackgroundColor,
    deleteSelected,
    deleteObjectById,
    saveCanvasState,
    undo,
    redo,
    exportCanvasAsHighDpiPNG,
    loadCanvasTemplateState,
    getCanvasSaveState,
    createAndPopulateHiddenCanvasState,
    selectObjectById,
    reorderCanvasObjectsBySort,
    setItemPosition,
    setItemProperties,
    copyObjectById,
    fitObjectToTemplateById,
    positionObjectToTemplateById
  };
};

const useCanvasController = (canvasRef, canvasOverlayRef, setHistoryState) => {
  const fabricRef = useRef(null);
  const fabricOverlayRef = useRef(null);
  const templateConfig = useRef(null);
  const {
    deleteSelected,
    resizeCanvas,
    setZoom,
    zoomToFit,
    setupGuides,
    setupPanAndZoom,
    setupObjectTracking,
    selectedObjectPosition,
    zoomLevel,
    addPersonalizedTextbox,
    addImageByUrl,
    changeBackgroundColor,
    saveCanvasState,
    undo,
    redo,
    exportCanvasAsHighDpiPNG,
    loadCanvasTemplateState,
    getCanvasSaveState,
    createAndPopulateHiddenCanvasState,
    selectObjectById,
    reorderCanvasObjectsBySort,
    setItemPosition,
    setItemProperties,
    deleteObjectById,
    copyObjectById,
    fitObjectToTemplateById,
    positionObjectToTemplateById
  } = useFabricCanvasActions();

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasRef.current, {
      backgroundColor: '#F0F0F0',
      retinaScaling: true,
      fireRightClick: true,
      stopContextMenu: true,
      centeredRotation: true
    });
    fabricRef.current = canvas;

    const overlayCanvas = new fabric.Canvas(canvasOverlayRef.current, {
      backgroundColor: 'transparent',
      selection: false,
      interactive: false,
      evented: false,
      skipTargetFind: true,
      fireRightClick: false,
      stopContextMenu: true,
      controlsAboveOverlay: true,
      enableRetinaScaling: true,
      centeredRotation: true
    });
    fabricOverlayRef.current = overlayCanvas;

    const resize = () => {
      resizeCanvas(canvasRef, fabricRef, fabricOverlayRef);
    };
    resize();
    window.addEventListener('resize', resize);

    setupPanAndZoom(canvas, overlayCanvas);
    setupGuides(canvas, overlayCanvas);
    setupObjectTracking(setHistoryState, canvas);

    const save = (e) => {
      saveCanvasState(setHistoryState, fabricRef.current);
    };
    canvas.on('object:added', save);
    canvas.on('object:modified', save);
    canvas.on('object:removed', save);

    const handleKeyDown = (e) => {
      if (e.key === 'Delete') {
        deleteSelected(setHistoryState, fabricRef.current);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      canvas.dispose();
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canvasRef]);

  return {
    setZoom: (zoom, point = null) =>
      setZoom(fabricRef.current, fabricOverlayRef.current, zoom, point),
    zoomToFit: () => zoomToFit(fabricRef.current, fabricOverlayRef.current),
    zoomLevel,
    canvas: fabricRef.current,
    templateConfig: templateConfig.current,
    selectedObjectPosition,
    addPersonalizedTextbox: (text, textProperties, options) =>
      addPersonalizedTextbox(fabricRef.current, text, textProperties, options),
    addImageByUrl: (url) =>
      addImageByUrl(
        fabricRef.current,
        url,
        undefined,
        undefined,
        templateConfig.current?.defaultPositioning
      ),
    changeBackgroundColor: (color) =>
      changeBackgroundColor(fabricRef.current, color),
    undo: () => undo(setHistoryState, fabricRef.current),
    redo: () => redo(setHistoryState, fabricRef.current),
    getExport: () => exportCanvasAsHighDpiPNG(fabricRef.current),
    loadCanvasTemplate: (config, onSuccessCallback) => {
      templateConfig.current = config;
      loadCanvasTemplateState(
        fabricRef.current,
        fabricOverlayRef.current,
        setHistoryState,
        config,
        onSuccessCallback
      );
    },
    getCanvasSaveState: (canvas = null) =>
      getCanvasSaveState(canvas || fabricRef.current),
    createAndPopulateHiddenCanvasState: (config, includePrintImage) =>
      createAndPopulateHiddenCanvasState(config, includePrintImage),
    selectObjectById: (id) => selectObjectById(fabricRef.current, id),
    reorderCanvasObjectsBySort: (layerData) => {
      reorderCanvasObjectsBySort(fabricRef.current, setHistoryState, layerData);
    },
    setItemPosition: (id, position) => {
      setItemPosition(setHistoryState, fabricRef.current, id, position);
    },
    setItemProperties: (id, properties, skipHistory = false) => {
      setItemProperties(
        setHistoryState,
        fabricRef.current,
        id,
        properties,
        skipHistory
      );
    },
    deleteObjectById: (id) => {
      deleteObjectById(setHistoryState, fabricRef.current, id);
    },
    copyObjectById: (id) => copyObjectById(fabricRef.current, id),
    fitObjectToTemplateById: (id, mode, withPadding) =>
      fitObjectToTemplateById(
        setHistoryState,
        fabricRef.current,
        id,
        mode,
        withPadding
      ),
    positionObjectToTemplateById: (id, position) =>
      positionObjectToTemplateById(
        setHistoryState,
        fabricRef.current,
        id,
        position,
        templateConfig.current?.defaultPositioning
      )
  };
};

const useQuickCanvasController = ({
  uploadUrlBase,
  onCreateDesignProduct
} = {}) => {
  const hasBeenCancelled = useRef(false);
  const [uploadState, setUploadState] = useState({
    uploadPks: [],
    completedPks: [],
    failedPks: [],
    responseData: [],
    loading: false,
    completed: false,
    cancelled: false
  });
  const { createAndPopulateHiddenCanvasState } = useFabricCanvasActions();

  const savePrintDesignsRecursively = (
    data,
    { storeDesignName, storeDesignImageUrl } = {}
  ) => {
    if (hasBeenCancelled.current) {
      setUploadState((prev) => ({ ...prev, loading: false }));
    }
    else if (data.length > 0) {
      onCreateDesignProduct(
        {
          storeDesignName,
          storeDesignImageUrl,
          printDesigns: [data[0]]
        },
        (responseData) => {
          setUploadState((prevState) => ({
            ...prevState,
            completedPks: [...prevState.completedPks, data[0].pk],
            responseData: [...prevState.responseData, ...responseData]
          }));

          savePrintDesignsRecursively([...data.slice(1)]);
        },
        () => {
          setUploadState((prevState) => ({
            ...prevState,
            failedPks: [...prevState.failedPks, data[0].pk]
          }));

          savePrintDesignsRecursively([...data.slice(1)]);
        }
      );
    }
    else {
      setUploadState((prev) => ({
        ...prev,
        loading: false,
        completed: true
      }));
    }
  };

  const saveDataUrlToStoragePromise = (dataUrl, fileName) =>
    new Promise((resolve, reject) => {
      dataUrlToFile(dataUrl, fileName, (file) => {
        if (file) {
          uploadFileToStorage(
            `${uploadUrlBase}-image-${fileName}`,
            file,
            (src) => {
              resolve(src);
            }
          );
        }
        else {
          reject(new Error('Failed to convert data URL to file')); // Reject if there's an issue
        }
      });
    });

  const onQuickCreateDesignProducts = ({
    pkMedia,
    imageUrl,
    ecomExternalProducts,
    storeDesignName,
    isPrivate
  }) => {
    setUploadState((prev) => ({
      ...prev,
      loading: true,
      completed: false,
      cancelled: false,
      uploadPks: ecomExternalProducts.map((ep) => ep.pkEcomExternalProduct),
      completedPks: [],
      failedPks: [],
      responseData: []
    }));

    Promise.all(
      ecomExternalProducts.map(async (ecomExternalProduct) => {
        const templateConfig =
          ecomExternalProduct.ecomExternalProductPrintTemplates.find(
            (t) => t.isDefault
          ) || ecomExternalProduct[0].ecomExternalProductPrintTemplates[0];

        const defaultPositioning = printDesignImagePositioningConfig.find(
          (config) =>
            (config.width === templateConfig?.imageWidthPx &&
              config.height === templateConfig?.imageHeightPx) ||
            (config.uuids &&
              config.uuids.includes(
                ecomExternalProduct.pkEcomExternalProduct.toString()
              ))
        )?.positioning?.[0];

        const {
          canvasState,
          imageUrl: printDesignPreview
        } = await createAndPopulateHiddenCanvasState(
          {
            templateImage: templateConfig?.svgSource,
            defaultPositioning,
            canvasState: null,
            imageUrl
          },
          true
        );
        const printImageUrl = await saveDataUrlToStoragePromise(
          printDesignPreview,
          `${ecomExternalProduct.pkEcomExternalProduct}-preview`
        );

        const designCollection = [].find((d) => d.pkMedia === pkMedia);

        return {
          pk: ecomExternalProduct.pkEcomExternalProduct,
          pkProduct: null,
          pkPrintDesign: null,
          pkDesign: designCollection?.pkDesign,
          productNameSuffix: designCollection
            ? `- ${designCollection.name}`
            : null,
          isPrivate,
          externalProductId: ecomExternalProduct.pkEcomExternalProduct.toString(),
          fkEcomExternalConnectionType:
            ecomExternalProduct.fkEcomExternalConnectionType,
          printImageUrl,
          designItems: canvasState.items.filter((i) => i.visible)
        };
      })
    )
      .then((data) => {
        savePrintDesignsRecursively(data, {
          storeDesignName,
          storeDesignImageUrl: imageUrl
        });
      })
      .catch((error) => {
        console.error('Error while processing templates:', error);
        triggerNotification('Error while processing the product templates.');
        setUploadState((prev) => ({ ...prev, loading: false }));
      });
  };

  const onCancelCreateDesignProducts = () => {
    hasBeenCancelled.current = true;
    setUploadState((prev) => ({
      ...prev,
      loading: false,
      cancelled: true
    }));
  };

  return {
    hasBeenCancelled: hasBeenCancelled.current,
    uploadState,
    onQuickCreateDesignProducts,
    onCancelCreateDesignProducts
  };
};

const getDefaultPrintTemplates = (ecomExternalProduct) => {
  const defaultPrintTemplatesMap = (
    ecomExternalProduct?.ecomExternalProductPrintTemplates || []
  ).reduce((map, current) => {
    const existing = map.get(current.fkEcomImagePosition);

    // Logic for replacement:
    // 1. No existing template for this position yet.
    // 2. Current is explicitly marked as default.
    // 3. Existing isn't a default, but current has better specs (SVG or larger area).
    const shouldReplace =
      !existing ||
      current.isDefault ||
      (!existing.isDefault &&
        (!!current.svgSource ||
          current.imageHeightPx * current.imageWidthPx >
            existing.imageHeightPx * existing.imageWidthPx));

    if (shouldReplace) {
      map.set(current.fkEcomImagePosition, current);
    }

    return map;
  }, new Map());

  return Array.from(defaultPrintTemplatesMap.values());
};

export {
  useCanvasController,
  useQuickCanvasController,
  getDefaultPrintTemplates,
  getDesignItemPropertiesObjectFromList
};
